'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Loader2, CheckCircle2 } from 'lucide-react';
import type { Locale } from '@/lib/i18n/config';
import { localizedHref } from '@/lib/i18n/href';
import { trackContactFormSubmit } from '@/lib/analytics';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  wantsUpdates: boolean;
  website: string; // honeypot
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Component-local strings, same pattern as components/tools/*.tsx — kept
// here rather than the shared dictionary since this copy is specific to
// this one form.
const T = {
  en: {
    errAllFields: 'Please fill in all fields.',
    errAgreePrivacy: 'Please agree to the Privacy Policy to send your message.',
    errValidEmail: 'Please enter a valid email address.',
    errMessageShort: 'Your message is a little short — please add a few more details.',
    errGeneric: 'Something went wrong. Please try again.',
    errNetwork: 'Network error. Please check your connection and try again.',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'E-mail',
    message: 'Message',
    agreePrefix: 'I agree with',
    privacyPolicy: 'Privacy Policy',
    agreeSuffix: 'to receive communications.',
    sending: 'Sending…',
    send: 'Send',
    messageSentTitle: 'Message sent',
    messageSentBodyPrefix: "Thanks for reaching out — we'll get back to you at",
    messageSentBodySuffix: 'as soon as we can.',
  },
  ar: {
    errAllFields: 'يرجى تعبئة جميع الحقول.',
    errAgreePrivacy: 'يرجى الموافقة على سياسة الخصوصية لإرسال رسالتك.',
    errValidEmail: 'يرجى إدخال بريد إلكتروني صالح.',
    errMessageShort: 'رسالتك قصيرة بعض الشيء — يرجى إضافة المزيد من التفاصيل.',
    errGeneric: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    errNetwork: 'خطأ في الاتصال. يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    email: 'البريد الإلكتروني',
    message: 'الرسالة',
    agreePrefix: 'أوافق على',
    privacyPolicy: 'سياسة الخصوصية',
    agreeSuffix: 'لتلقي المراسلات.',
    sending: 'جارٍ الإرسال…',
    send: 'إرسال',
    messageSentTitle: 'تم إرسال الرسالة',
    messageSentBodyPrefix: 'شكرًا لتواصلك معنا — سنعاود التواصل معك على',
    messageSentBodySuffix: 'في أقرب وقت ممكن.',
  },
  // Machine-translated (2026-08) — UI labels/errors, not legal text, so
  // lower risk than the privacy/terms pages. Still worth a native-speaker
  // pass eventually, same as the Arabic copy above.
  es: {
    errAllFields: 'Por favor, completa todos los campos.',
    errAgreePrivacy: 'Por favor, acepta la Política de Privacidad para enviar tu mensaje.',
    errValidEmail: 'Por favor, introduce una dirección de correo válida.',
    errMessageShort: 'Tu mensaje es un poco corto — añade más detalles, por favor.',
    errGeneric: 'Algo salió mal. Inténtalo de nuevo.',
    errNetwork: 'Error de red. Comprueba tu conexión e inténtalo de nuevo.',
    firstName: 'Nombre',
    lastName: 'Apellido',
    email: 'Correo electrónico',
    message: 'Mensaje',
    agreePrefix: 'Acepto la',
    privacyPolicy: 'Política de Privacidad',
    agreeSuffix: 'para recibir comunicaciones.',
    sending: 'Enviando…',
    send: 'Enviar',
    messageSentTitle: 'Mensaje enviado',
    messageSentBodyPrefix: 'Gracias por contactarnos — te responderemos a',
    messageSentBodySuffix: 'lo antes posible.',
  },
  // Machine-translated (2026-08) — UI labels/errors, not legal text.
  ru: {
    errAllFields: 'Пожалуйста, заполните все поля.',
    errAgreePrivacy: 'Пожалуйста, согласитесь с Политикой конфиденциальности, чтобы отправить сообщение.',
    errValidEmail: 'Пожалуйста, введите действительный адрес электронной почты.',
    errMessageShort: 'Ваше сообщение слишком короткое — добавьте, пожалуйста, немного больше деталей.',
    errGeneric: 'Что-то пошло не так. Пожалуйста, попробуйте ещё раз.',
    errNetwork: 'Ошибка сети. Проверьте подключение и попробуйте ещё раз.',
    firstName: 'Имя',
    lastName: 'Фамилия',
    email: 'Эл. почта',
    message: 'Сообщение',
    agreePrefix: 'Я согласен(-на) с',
    privacyPolicy: 'Политикой конфиденциальности',
    agreeSuffix: 'на получение уведомлений.',
    sending: 'Отправка…',
    send: 'Отправить',
    messageSentTitle: 'Сообщение отправлено',
    messageSentBodyPrefix: 'Спасибо, что написали нам — мы свяжемся с вами по адресу',
    messageSentBodySuffix: 'как можно скорее.',
  },
  fr: {
    errAllFields: 'Veuillez remplir tous les champs.',
    errAgreePrivacy: 'Veuillez accepter la Politique de confidentialité pour envoyer votre message.',
    errValidEmail: 'Veuillez saisir une adresse e-mail valide.',
    errMessageShort: 'Votre message est un peu court — veuillez ajouter quelques détails supplémentaires.',
    errGeneric: "Une erreur s'est produite. Veuillez réessayer.",
    errNetwork: 'Erreur réseau. Veuillez vérifier votre connexion et réessayer.',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'E-mail',
    message: 'Message',
    agreePrefix: "J'accepte la",
    privacyPolicy: 'Politique de confidentialité',
    agreeSuffix: 'pour recevoir des communications.',
    sending: 'Envoi…',
    send: 'Envoyer',
    messageSentTitle: 'Message envoyé',
    messageSentBodyPrefix: 'Merci de nous avoir contactés — nous vous répondrons à',
    messageSentBodySuffix: 'dès que possible.',
  },
} as const;

const initialState: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  message: '',
  wantsUpdates: false,
  website: '',
};

export default function ContactForm({ locale = 'en' }: { locale?: Locale }) {
  const t = T[locale] ?? T.en;
  const [form, setForm] = useState<FormState>(initialState);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.message.trim()) {
      setError(t.errAllFields);
      return;
    }
    if (!form.wantsUpdates) {
      setError(t.errAgreePrivacy);
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setError(t.errValidEmail);
      return;
    }
    if (form.message.trim().length < 10) {
      setError(t.errMessageShort);
      return;
    }

    setStatus('submitting');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || t.errGeneric);
        setStatus('error');
        return;
      }

      setStatus('success');
      trackContactFormSubmit();
    } catch {
      setError(t.errNetwork);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16 px-4">
        <div className="p-4 bg-accent/10 rounded-full mb-5">
          <CheckCircle2 className="h-9 w-9 text-accent" />
        </div>
        <h3 className="text-2xl font-bold text-foreground heading mb-2">{t.messageSentTitle}</h3>
        <p className="text-sm text-textSecondary max-w-sm">
          {t.messageSentBodyPrefix} <span className="text-foreground">{form.email}</span> {t.messageSentBodySuffix}
        </p>
      </div>
    );
  }

  const inputClass =
    'w-full px-5 py-3.5 bg-bg3 border border-borderDark rounded-xl text-sm text-foreground placeholder-textSecondary focus:outline-none focus:border-accent/60 focus:shadow-glow-purple transition-[border-color,box-shadow]';

  const isFormValid =
    form.firstName.trim().length > 0 &&
    form.lastName.trim().length > 0 &&
    EMAIL_RE.test(form.email.trim()) &&
    form.message.trim().length >= 10 &&
    form.wantsUpdates;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {/* Honeypot field — hidden from sighted users and screen readers, real visitors never touch it */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={(e) => update('website', e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute -left-[9999px] top-0 w-px h-px opacity-0"
      />

      <input
        type="text"
        placeholder={t.firstName}
        value={form.firstName}
        onChange={(e) => update('firstName', e.target.value)}
        maxLength={60}
        className={inputClass}
      />

      <input
        type="text"
        placeholder={t.lastName}
        value={form.lastName}
        onChange={(e) => update('lastName', e.target.value)}
        maxLength={60}
        className={inputClass}
      />

      <input
        type="email"
        placeholder={t.email}
        value={form.email}
        onChange={(e) => update('email', e.target.value)}
        maxLength={100}
        className={inputClass}
      />

      <textarea
        placeholder={t.message}
        value={form.message}
        onChange={(e) => update('message', e.target.value)}
        maxLength={5000}
        rows={5}
        className={`${inputClass} resize-none`}
      />

      <label className="flex items-start gap-3 cursor-pointer select-none pt-1">
        <input
          type="checkbox"
          checked={form.wantsUpdates}
          onChange={(e) => update('wantsUpdates', e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-borderDark bg-bg3 accent-[#7B2FF7] cursor-pointer"
        />
        <span className="text-sm text-textSecondary">
          {t.agreePrefix}{' '}
          <Link href={localizedHref(locale, '/privacy')} className="text-foreground underline hover:text-accent transition-colors">
            {t.privacyPolicy}
          </Link>{' '}
          {t.agreeSuffix}
        </span>
      </label>

      {error && (
        <p className="text-sm badge-err rounded-lg px-4 py-2.5">
          {error}
        </p>
      )}

      <div className="flex justify-end pt-1">
        <button
          type="submit"
          disabled={status === 'submitting' || !isFormValid}
          className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-accent hover:bg-accentHover disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl transition-colors min-w-[120px]"
        >
          {status === 'submitting' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> {t.sending}
            </>
          ) : (
            t.send
          )}
        </button>
      </div>
    </form>
  );
}
